import React, {useEffect, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import {supabase} from "./utils/supabaseClient";
import {
    Box,
    Button,
    Center,
    Container,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Select,
    Stack,
    Text,
    Badge
} from "@chakra-ui/react";
import {CheckCircleIcon} from "@chakra-ui/icons";
import SignaturePad from "react-signature-canvas";
import moment from "moment";
import { COLORS } from "./styles/colors";

export const Attendance = () => {
    const [attendees, setAttendees] = useState([]);
    const [name, setName] = useState("");
    const [submitting, setSubmitting] = useState(false)
    const [success, setSuccess] = useState(null)
    const [clickCount, setClickCount] = useState(0);
    const { attendanceId } = useParams();
    const sigPad = useRef(null);
    const [signedAttendees, setSignedAttendees] = useState([]);
    const [courseSessions, setCourseSessions] = useState([]);
    const [courseTitle, setCourseTitle] = useState("");
    const [courseRunId, setCourseRunId] = useState("");

    

    const getAllAttendees = async () => {
        const { data, error } = await supabase
            .from("course_run")
            .select("*, registration_modules(*, registration(*)), course_type(*)")
            .eq("attendance_id", attendanceId)
            .single();

        if (data) {
            console.log(data);
            setAttendees(data.registration_modules.map(module => module.registration));
            setCourseTitle(data.course_type.name);
            setCourseRunId(data.id);

            // Fetch sessions for this course run
            const { data: sessions, error: sessionsError } = await supabase
                .from("session")
                .select("*")
                .eq("course_run_code", data.id)
                .order("session_date", { ascending: true });

            if (sessionsError) {
                console.error("Error fetching sessions:", sessionsError);
            }

            // Transform sessions to match the old course_dates format
            const course_dates = (sessions || []).map(session => ({
                date: session.session_date,
                start_time: session.start_time ? moment(session.start_time, "HH:mm:ss").format("HHmm") : null,
                end_time: session.end_time ? moment(session.end_time, "HH:mm:ss").format("HHmm") : null,
                room: session.custom_room || session.room_id,
                venue: session.custom_venue || session.venue_id
            }));

            setCourseSessions(course_dates);
        }
        if (error) {
            console.log(error);
        }
    };

    const getSignedAttendees = async () => {
        // Find active session
        const activeSession = courseSessions.find(session => {
            const now = moment();
            const sessionStart = moment(`${session.date} ${session.start_time}`);
            const sessionEnd = moment(`${session.date} ${session.end_time}`);
            return now.isBetween(sessionStart, sessionEnd);
        });

        if (activeSession) {
            const sessionStart = moment(`${activeSession.date} ${activeSession.start_time}`).format();
            const sessionEnd = moment(`${activeSession.date} ${activeSession.end_time}`).format();
            
            const { data, error } = await supabase
                .from('attendance')
                .select('registration_id, created_at')
                .eq('attendance_id', attendanceId)
                .gte('created_at', sessionStart)
                .lte('created_at', sessionEnd);

            if (data) {
                setSignedAttendees(data.map(item => item.registration_id));
            }
            if (error) {
                console.log(error);
            }
        } else {
            // No active session, clear signed attendees
            setSignedAttendees([]);
        }
    };

    const insertAttendance = async(signature) => {
        setSubmitting(true)
        const { data, error } = await supabase
            .from('attendance')
            .insert([
                { attendance_id: attendanceId, registration_id: name, signature: signature, course_run_id: courseRunId },
            ])
            .select()
            .single()
        if (data) {
            setSubmitting(false)
            setSuccess(data)
            getSignedAttendees();
            console.log(data)
        }

        if (error) {
            console.log(error)
            setSubmitting(false)
        }
    }

    useEffect(() => {
        getAllAttendees();
        getSignedAttendees();
    }, [attendanceId]);

    const clearSignature = () => {
        sigPad.current.clear();
    };

    const saveSignature = () => {
        if (!name) {
            alert("Please select a name before submitting.");
            return; // Prevent submission if no name is selected
        }
        if (sigPad.current?.isEmpty()) {
            alert("Please sign before submitting."); // Optional: Replace with a toast notification
            return; // Prevent submission if the signature pad is empty
        }
        if (sigPad.current) {
            const dataURL = sigPad.current.getTrimmedCanvas().toDataURL('image/png');
            console.log(name)
            console.log(dataURL);
            insertAttendance(dataURL)
            // You can now send this dataURL to your server or use it as needed
        }
    };

    const isCourseToday = (startDate, endDate) => {
        const today = moment().startOf('day');
        const courseStart = moment(startDate).startOf('day');
        const courseEnd = moment(endDate).startOf('day');
        return today.isBetween(courseStart, courseEnd, null, '[]') || 
               today.isSame(courseStart) || 
               today.isSame(courseEnd);
    };

    const courseToday = attendees[0]?.course_run_details ? 
        isCourseToday(attendees[0].course_run_details.start_date, attendees[0].course_run_details.end_date) : 
        false;

    const handleBackdoorClick = () => {
        setClickCount(prev => prev + 1);
    };

    const isBackdoorActive = clickCount >= 5;

    return (
        success ? (
            <Container 
                maxW="container.lg" 
                py={8}
                minH="100vh"
            >
                <Box 
                    bg="white" 
                    borderRadius="2xl" 
                    boxShadow="sm" 
                    p={8}
                    mb={8}
                >
                    <Stack spacing="4" width="100%" textAlign="center">
                        <Heading size="md">Submission success</Heading>
                        <Center>
                            <CheckCircleIcon boxSize={15} color="green.500" />
                        </Center>
                        <Text>
                            Attendance received from {attendees.find(each => each.id === success.registration_id)?.participant_name} on {moment(success.created_at).format('DD MMM YYYY')} at {moment(success.created_at).format('HH:mm')}
                        </Text>
                    </Stack>
                </Box>
            </Container>
        ) : (
            <Container 
                maxW="container.lg" 
                py={8}
                minH="100vh"
            >
                <Box 
                    bg="white" 
                    borderRadius="2xl" 
                    boxShadow="sm" 
                    p={8}
                    mb={8}
                >
                    <Stack spacing="6">
                        <Heading 
                            size="xs"
                            mb={4}
                            fontWeight="semibold"
                            borderBottom="2px solid"
                            pb={2}
                            width="fit-content"
                        >
                            Attendance for {courseTitle}
                        </Heading>

                        {/* <Text fontSize="15px" fontWeight="bold">
                            Course Dates: {moment(attendees[0]?.course_run_details?.start_date).format("DD MMM YYYY")} - {moment(attendees[0]?.course_run_details?.end_date).format("DD MMM YYYY")}
                        </Text> */}

                        <Text fontSize="15px" fontWeight="bold">
                        Current Course Session: {(() => {
                                const activeSession = courseSessions.find(session => {
                                    const now = moment();
                                    const sessionStart = moment(`${session.date} ${session.start_time}`);
                                    const sessionEnd = moment(`${session.date} ${session.end_time}`);
                                    return now.isBetween(sessionStart, sessionEnd);
                                });
                                
                                return activeSession 
                                    ? `${moment(activeSession.date).format("DD MMM YYYY")} (${activeSession.start_time} - ${activeSession.end_time})`
                                    : 'No active session';
                            })()}
                        </Text>


                        <FormControl>
                            <FormLabel fontWeight="bold">Name</FormLabel>
                            <Box>
                                <Select 
                                    value={name} 
                                    placeholder="Select your name" 
                                    bg="white"
                                    onChange={(e) => setName(e.target.value)}
                                >
                                    {attendees.map(each => (
                                        <option 
                                            key={each.id} 
                                            value={each.id}
                                            style={{
                                                color: signedAttendees.includes(each.id) ? "green.700" : "brand.700",
                                                backgroundColor: signedAttendees.includes(each.id) ? '#f0f9f4' : 'white',
                                                fontWeight: signedAttendees.includes(each.id) ? '500' : 'normal'
                                            }}
                                        >
                                            {signedAttendees.includes(each.id) ? `(Signed today) ` : ''}{each.participant_name}
                                        </option>
                                    ))}
                                </Select>
                                <Box 
                                    mt={2} 
                                    p={3} 
                                    bg="white" 
                                    borderRadius="md" 
                                    border="1px solid"
                                >
                                    <Text 
                                        fontSize="sm" 
                                        textAlign="center"
                                    >
                                        <Text as="span" color={"green.700"} fontWeight="bold">
                                            {signedAttendees.length}
                                        </Text>
                                        <Text as="span" color={"brand.700"}>
                                            {' '}out of{' '}
                                        </Text>
                                        <Text as="span" color={"brand.700"} fontWeight="bold">
                                            {attendees.length}
                                        </Text>
                                        <Text as="span" color={"brand.700"}>
                                            {' '}participants have signed in for this session
                                        </Text>
                                    </Text>
                                </Box>
                            </Box>
                        </FormControl>

                        {name && (
                            <>
                                <Box 
                                    borderRadius="lg"
                                    p={4}
                                    textAlign="center"
                                    border="1px solid"
                                >
                                    <Text 
                                        fontSize="md"
                                        fontWeight="bold"
                                        mb={1}
                                    >
                                        Hello {attendees.find(each => each.id === name)?.participant_name}! 
                                    </Text>
                                    <Text
                                        fontSize="sm"
                                        fontWeight="400"
                                    >
                                        Glad to have you today! 😊
                                        <br />
                                        Please sign below <Text as="span" color={"green.700"}>⬇️</Text>
                                    </Text>
                                </Box>

                                <FormControl>
                                    <FormLabel fontWeight="bold">Signature</FormLabel>
                                    <Box 
                                        border="1px solid"
                                        borderRadius="md"
                                        overflow="hidden"
                                        width="100%"
                                        height="200px"
                                    >
                                        <SignaturePad 
                                            ref={sigPad} 
                                            canvasProps={{ 
                                                className: 'signatureCanvas',
                                                width: '900px',
                                                height: '200px'
                                            }} 
                                        />
                                    </Box>
                                </FormControl>

                                <Button 
                                    mt={2}
                                    size="sm"
                                    variant="outline"
                                    onClick={clearSignature}
                                    alignSelf="flex-start"
                                >
                                    Clear Signature
                                </Button>
                            </>
                        )}

                        {!courseToday && (
                            <Text 
                                color={"brand.700"} 
                                fontSize="sm" 
                                textAlign="center" 
                                mb={4}
                                fontStyle="italic"
                                onClick={handleBackdoorClick}
                                cursor="pointer"
                            >
                                "Hold your horses! 🐎 This course isn't happening today. 
                                Come back when it's showtime!"
                                {isBackdoorActive && (
                                    <Text fontSize="xs" color={"green.700"} mt={1}>
                                        Backdoor activated! You can now submit.
                                    </Text>
                                )}
                            </Text>
                        )}

                        {name && (
                            <Button 
                                isLoading={submitting} 
                                onClick={saveSignature}
                                size="lg"
                                px={12}
                                isDisabled={!courseToday && !isBackdoorActive}
                                alignSelf="center"
                            >
                                Submit
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Container>
        )
    );
};
